import React, { useState } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import SidebarMenuArrowIcon from '@icons/sidebar-menu-arrow.svg';
import { useAuthActions } from 'src/utils/hooks/useAuthActions';
import { useSidebarStore } from '../../stores/SidebarStore';
import AgreementModal from '../AgreementModal/AgreementModal';
import { useGetActiveAgreements } from '../../shared/queries/useGetActiveAgreements';
import { AgreementIcons, AgreementShortName, IAgreement } from '../../shared/types/Agreement';
import { useHeaderStore } from 'src/stores/HeaderStore';
import ProfileImage from '../ProfileImage/ProfileImage';
import styles from './Sidebar.module.scss';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import { BAL_URLS } from 'src/utils/constants/common';
import { PassportClickInterceptor } from '../PassportClickInterceptor/PassportClickInterceptor';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';
import { useThemeStore } from 'src/stores/ThemeStore';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FeatureFlags } from 'src/utils/constants/FeatureFlags';

interface MenuItem {
  name: string;
  icon: string;
  url?: string;
  isMobileOnly?: boolean;
  visible?: boolean;
  onClick?: () => void;
  paramsToClear?: string[];
}

const MenuItem = React.memo((item: MenuItem) => {
  const { pathname } = useLocation();
  const { navigate } = useNavigateWithSecureParams();
  const { toggleMobileMenu } = useSidebarStore();
  const { isMobile } = useScreenSize();

  return (
    <PassportClickInterceptor>
      <div
        key={item.name}
        className={classNames(styles['menu-item'], {
          [styles['menu-item-active']]: item.url && pathname === item.url,
        })}
        onClick={() => {
          if (item.url) {
            navigate(item.url, { paramsToClear: item.paramsToClear || [] });
          }
          if (item.onClick) {
            item.onClick();
          }
          isMobile && toggleMobileMenu();
        }}
      >
        <div className={classNames(styles['menu-item-icon'], styles[item.icon])}></div>
        {<span>{item.name}</span>}
      </div>
    </PassportClickInterceptor>
  );
});

const Sidebar = () => {
  const { isMobile } = useScreenSize();
  const { logout } = useAuthActions();
  const { data: userInfo } = useGetUser();
  const { isCollapsed, isMobileMenuOpen, toggleSidebar } = useSidebarStore();
  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);
  const [agreementId, setAgreementId] = useState<number>();
  const { data } = useGetActiveAgreements();
  const { openChangePasswordModal, openUploadProfilePictureModal, openSwitchRolesModal } = useHeaderStore();
  const showDualRoleButton = (userInfo?.linkedUsers ?? []).length > 0;
  const agreements = data || [];
  const { isDeloitteTheme } = useThemeStore();
  const flags = useFlags();

  const openNewsPage = () => {
    window.open(BAL_URLS.News, '_blank');
  };

  const TopMenuItems: MenuItem[] = [
    { name: 'Home', icon: 'home-icon', url: '/home' },
    { name: 'Cases', icon: 'cases-icon', url: '/cases' },
    { name: 'All Documents', icon: 'documents-icon', url: '/documents' },
    { name: 'Immigration Status', icon: 'immigration-status-icon', url: '/immigration-status', paramsToClear: ['id'] },
    { name: 'Profile', icon: 'profile-icon', url: '/user-profile' },
  ];

  const BottomMenuItems = [
    { name: 'My Preferences', icon: 'my-preference-icon', url: '/user-preferences', isMobileOnly: true },
    {
      name: 'My Roles',
      icon: 'beneficiary-roles-icon',
      isMobileOnly: true,
      onClick: openSwitchRolesModal,
      visible: showDualRoleButton,
    },
    { name: 'Change Password', icon: 'change-password-icon', isMobileOnly: true, onClick: openChangePasswordModal },
    {
      name: 'Change Profile Picture',
      icon: 'change-profile-picture-icon',
      isMobileOnly: true,
      onClick: openUploadProfilePictureModal,
    },
    ...(flags[FeatureFlags.FNXHideSherpa] && !isDeloitteTheme
      ? [{ name: 'Visa & Travel', icon: 'map-pinline-icon', url: '/visa-and-travel' }]
      : []),
    ...(isDeloitteTheme ? [] : [{ name: 'News', icon: 'news-icon', onClick: openNewsPage }]),
  ];

  const openAgreementModal = () => {
    setIsAgreementModalOpen(true);
  };

  const closeAgreementModal = () => {
    setIsAgreementModalOpen(false);
  };

  const handleClickAgreement = (agreementId: number) => () => {
    setAgreementId(agreementId);
    openAgreementModal();
  };

  const getMenuItemPropsForAgreement = (agreement: IAgreement) => {
    switch (agreement.applicationShortName) {
      case AgreementShortName.CAPSCD:
      case AgreementShortName.CAPSCPDOSPI:
        return { name: 'Consent Agreement', icon: AgreementIcons.CAPSCD };
      case AgreementShortName.PP:
        return { name: agreement.applicationLicenseName, icon: AgreementIcons.PP };
      case AgreementShortName.OSA:
      default:
        return { name: agreement.applicationLicenseName, icon: AgreementIcons.DEFAULT };
    }
  };

  return (
    <nav
      className={classNames(styles['sidebar'], isDeloitteTheme ? styles['sidebar-deloitte'] : styles['sidebar-bal'], {
        [styles['sidebar-collapsed']]: isCollapsed,
        [styles['sidebar-mobile-menu-open']]: isMobileMenuOpen,
      })}
      id="sidebar"
    >
      <div className={styles['side-panel-toggle-icon']} onClick={() => toggleSidebar()}>
        <img src={SidebarMenuArrowIcon} />
      </div>

      {isMobile && (
        <div className={styles['sidebar-user-info']}>
          <ProfileImage />
          <span>Welcome, {userInfo?.fullName}</span>
        </div>
      )}

      <div className={classNames(styles['sidebar-menu'], styles['sidebar-top-menu'])}>
        <div>{TopMenuItems.map((item) => item.visible !== false && <MenuItem key={item.name} {...item} />)}</div>
        <div className={styles['bottom-menu-items-container']}>
          <div className={styles['menu-divider']} />
          {BottomMenuItems.map((item) =>
            (!isMobile && item.isMobileOnly) || item.visible === false ? null : <MenuItem key={item.name} {...item} />
          )}
          {agreements
            .filter(
              (agreement) =>
                !isDeloitteTheme ||
                ![AgreementShortName.PP, AgreementShortName.CAPSCD, AgreementShortName.CAPSCPDOSPI].includes(
                  agreement?.applicationShortName as AgreementShortName
                )
            )
            .map((agreement) => {
              const itemProps = getMenuItemPropsForAgreement(agreement);
              return (
                <MenuItem
                  key={agreement.applicationLicenseName}
                  {...itemProps}
                  onClick={handleClickAgreement(agreement.applicationLicenseId)}
                />
              );
            })}
          {isMobile && <MenuItem key={'Logout'} name="Logout" icon="logout-icon" onClick={logout} />}
        </div>
      </div>

      <AgreementModal agreementId={agreementId} visible={isAgreementModalOpen} onModalClosed={closeAgreementModal} />
    </nav>
  );
};

export default Sidebar;
